'use client';

import * as React from 'react';
import Autoplay from 'embla-carousel-autoplay';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '~/components/ui/carousel';
import { cn } from '~/lib/utils';
import {
  getResponsiveValues,
  type ResponsivePropertyObject,
} from '~/utils/content-utils/responsive-properties';
import { type CallToActionButtonStyle } from '../umbraco/call-to-action';

type ContentCarouselProps = {
  maxVisibleItems: ResponsivePropertyObject<number>;
  items?: React.ReactNode[];
  buttonVerticalPosition?: 'high' | 'normal';
  buttonHorizontalPosition?: 'inside' | 'outside';
  className?: string;
  loop?: boolean;
  center?: boolean;
  autoPlay?: boolean;
  autoPlayDelay?: number;
  callToActionButtonStyle?: CallToActionButtonStyle;
  callToActionButtonClassName?: string | null;
};

type CarouselStyle = {
  '--item-basis'?: string;
  '--item-basis-sm'?: string;
  '--item-basis-md'?: string;
  '--item-basis-lg'?: string;
  '--item-basis-xl'?: string;
  '--item-basis-2xl'?: string;
} & React.CSSProperties;

export function ContentCarousel({
  items,
  maxVisibleItems,
  buttonVerticalPosition,
  buttonHorizontalPosition,
  className,
  loop,
  center,
  autoPlay,
  autoPlayDelay = 5000,
  callToActionButtonStyle,
  callToActionButtonClassName,
}: ContentCarouselProps) {
  const maxVisibleItemCounts = getResponsiveValues(maxVisibleItems, 1);

  const style: CarouselStyle = {
    '--item-basis': (1 / maxVisibleItemCounts.default) * 100 + '%' || undefined,
    '--item-basis-sm': (1 / maxVisibleItemCounts.sm) * 100 + '%' || undefined,
    '--item-basis-md':
      (1 / (maxVisibleItemCounts.md ?? 1)) * 100 + '%' || undefined,
    '--item-basis-lg':
      (1 / (maxVisibleItemCounts.lg ?? 1)) * 100 + '%' || undefined,
    '--item-basis-xl':
      (1 / (maxVisibleItemCounts.xl ?? 1)) * 100 + '%' || undefined,
    '--item-basis-2xl':
      (1 / (maxVisibleItemCounts.xxl ?? 1)) * 100 + '%' || undefined,
  };

  const itemCount = items?.length ?? 0;

  const carouselActive = {
    default: maxVisibleItemCounts.default >= itemCount,
    sm: maxVisibleItemCounts.sm >= itemCount,
    md: maxVisibleItemCounts.md >= itemCount,
    lg: maxVisibleItemCounts.lg >= itemCount,
    xl: maxVisibleItemCounts.xl >= itemCount,
    xxl: maxVisibleItemCounts.xxl >= itemCount,
  };

  if (itemCount === 0) {
    return null;
  }

  return (
    <Carousel
      plugins={
        autoPlay
          ? [
              Autoplay({
                delay: autoPlayDelay,
                stopOnFocusIn: true,
                stopOnMouseEnter: true,
                stopOnInteraction: false,
              }),
            ]
          : undefined
      }
      opts={{
        loop,
        active: !carouselActive.default,
        align: center ? 'center' : 'start',
        breakpoints: {
          '(min-width: 640px) and (max-width:767px)': {
            // sm
            active: !carouselActive.sm,
            slidesToScroll: loop ? 1 : Math.floor(maxVisibleItemCounts.sm),
          },
          '(min-width: 768px) and (max-width: 1023px)': {
            //md
            active: !carouselActive.md,
            slidesToScroll: loop ? 1 : Math.floor(maxVisibleItemCounts.md),
          },
          '(min-width: 1024px) and (max-width: 1279px)': {
            //lg
            active: !carouselActive.lg,
            slidesToScroll: loop ? 1 : Math.floor(maxVisibleItemCounts.lg),
          },
          '(min-width: 1280px) and (max-width: 1536px)': {
            //xl
            active: !carouselActive.xl,
            slidesToScroll: loop ? 1 : Math.floor(maxVisibleItemCounts.xl),
          },
          '(min-width: 1536px)': {
            //2xl
            active: !carouselActive.xxl,
            slidesToScroll: loop ? 1 : Math.floor(maxVisibleItemCounts.xxl),
          },
        },
      }}
      className={cn('w-full', className)}
    >
      <CarouselContent>
        {items?.map((item, index) => (
          <CarouselItem
            style={style}
            key={index}
            className="basis-[var(--item-basis)] items-stretch sm:basis-[var(--item-basis-sm)] md:basis-[var(--item-basis-md)] lg:basis-[var(--item-basis-lg)] xl:basis-[var(--item-basis-xl)] 2xl:basis-[var(--item-basis-2xl)]"
          >
            {item}
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious
        style={callToActionButtonStyle}
        className={cn(
          'hidden size-[3.5rem] disabled:pointer-events-auto md:flex [&>svg]:size-12 [&>svg]:stroke-1',
          buttonHorizontalPosition === 'outside'
            ? '-left-4 lg:-left-8'
            : 'left-4',
          callToActionButtonClassName,
          {
            'top-[calc(25%-1rem)]': buttonVerticalPosition === 'high',
            'sm:hidden': carouselActive.sm,
            'md:hidden': carouselActive.md,
            'lg:hidden': carouselActive.lg,
            'xl:hidden': carouselActive.xl,
            '2xl:hidden': carouselActive.xxl,
          },
        )}
        variant={callToActionButtonStyle ? 'custom' : 'outline'}
      />
      <CarouselNext
        style={callToActionButtonStyle}
        className={cn(
          'hidden size-[3.5rem] disabled:pointer-events-auto md:flex [&>svg]:size-12 [&>svg]:stroke-1',
          buttonHorizontalPosition === 'outside'
            ? '-right-4 lg:-right-8'
            : 'right-4',
          callToActionButtonClassName,
          {
            'top-[calc(25%-1rem)]': buttonVerticalPosition === 'high',
            'sm:hidden': carouselActive.sm,
            'md:hidden': carouselActive.md,
            'lg:hidden': carouselActive.lg,
            'xl:hidden': carouselActive.xl,
            '2xl:hidden': carouselActive.xxl,
          },
        )}
        variant={callToActionButtonStyle ? 'custom' : 'outline'}
      />
    </Carousel>
  );
}
